import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import './Categorias.css';

const Categorias = () => {
  const [modalAberto, setModalAberto] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  const queryClient = useQueryClient();

  // Buscar estabelecimento
  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-categorias',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            return response.data.data.estabelecimento;
          } catch (padraoError) {
            return null;
          }
        }
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  const estabelecimento = estabelecimentoData?._id;

  // Buscar categorias
  const { data, isLoading, refetch } = useQuery(
    ['categorias', estabelecimento],
    async () => {
      if (!estabelecimento) return [];
      const response = await api.get(`/api/categorias?estabelecimento=${estabelecimento}`);
      return response.data.data.categorias || [];
    },
    {
      enabled: !!estabelecimento,
      retry: 1
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/api/categorias/${id}`),
    {
      onSuccess: () => {
        toast.success('Categoria removida!');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao remover categoria');
      }
    }
  );

  const handleDelete = (categoria) => {
    if (window.confirm(`Tem certeza que deseja remover a categoria "${categoria.nome}"?`)) {
      deleteMutation.mutate(categoria._id);
    }
  };

  if (isLoading) {
    return <div className="loading">Carregando categorias...</div>;
  }

  return (
    <div className="categorias-page">
      <div className="page-header">
        <div>
          <h2>Categorias</h2>
          <p>Gerencie as categorias dos seus produtos</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            setCategoriaEditando(null);
            setModalAberto(true);
          }}
        >
          <Plus size={20} />
          Nova Categoria
        </button>
      </div>

      {!estabelecimento ? (
        <div style={{ padding: '2rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '0.5rem' }}>
          <h3 style={{ color: '#856404', marginBottom: '0.5rem' }}>⚠️ Estabelecimento não encontrado</h3>
          <p style={{ color: '#856404' }}>
            Configure um estabelecimento primeiro em Configurações.
          </p>
        </div>
      ) : (
        <>
          {data && data.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', background: '#f9fafb', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '1rem' }}>
                Nenhuma categoria cadastrada ainda.
              </p>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setCategoriaEditando(null);
                  setModalAberto(true);
                }}
              >
                <Plus size={20} />
                Criar Primeira Categoria
              </button>
            </div>
          ) : (
            <div className="categorias-grid">
              {data?.map(categoria => (
                <div key={categoria._id} className="categoria-card">
                  <div className="categoria-card-header">
                    <h3>{categoria.nome}</h3>
                    <div className="categoria-card-actions">
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setCategoriaEditando(categoria);
                          setModalAberto(true);
                        }}
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDelete(categoria)}
                        title="Remover"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  {categoria.descricao && (
                    <p className="categoria-descricao">{categoria.descricao}</p>
                  )}
                  <div className="categoria-footer">
                    <span className={`badge ${categoria.ativa ? 'badge-success' : 'badge-danger'}`}>
                      {categoria.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {modalAberto && (
        <ModalCategoria
          categoria={categoriaEditando}
          estabelecimento={estabelecimento}
          onClose={() => {
            setModalAberto(false);
            setCategoriaEditando(null);
          }}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
    </div>
  );
};

const ModalCategoria = ({ categoria, estabelecimento, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: categoria?.nome || '',
    descricao: categoria?.descricao || '',
    ativa: categoria?.ativa !== false,
    ordem: categoria?.ordem || 0
  });

  const queryClient = useQueryClient();

  const saveMutation = useMutation(
    (data) => {
      if (categoria) {
        return api.put(`/api/categorias/${categoria._id}`, data);
      }
      return api.post('/api/categorias', { ...data, estabelecimento });
    },
    {
      onSuccess: () => {
        toast.success(categoria ? 'Categoria atualizada!' : 'Categoria criada!');
        queryClient.invalidateQueries('categorias');
        onSuccess();
        onClose();
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar categoria';
        const errors = error.response?.data?.errors;
        
        if (errors && Array.isArray(errors)) {
          errors.forEach(err => {
            toast.error(err.msg || err.message || 'Erro de validação');
          });
        } else {
          toast.error(errorMessage);
        }
        console.error('Erro ao salvar categoria:', error);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nome || formData.nome.trim() === '') {
      toast.error('Nome da categoria é obrigatório');
      return;
    }
    
    saveMutation.mutate(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{categoria ? 'Editar Categoria' : 'Nova Categoria'}</h3>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome da Categoria *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
              placeholder="Ex: Lanches, Bebidas, Sobremesas"
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              rows={3}
              placeholder="Descrição opcional da categoria"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.ativa}
                  onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                />
                {' '}Categoria ativa
              </label>
            </div>
            <div className="form-group">
              <label>Ordem (opcional)</label>
              <input
                type="number"
                min="0"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <small>Use para ordenar as categorias (menor número aparece primeiro)</small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saveMutation.isLoading}>
              <Save size={18} />
              {saveMutation.isLoading ? 'Salvando...' : categoria ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Categorias;










